import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React from 'react';

import {BaseSeedDataConfig, SeedDataConfigType} from 'analysis-tool/create-analysis-modal/SeedDataTypes';
import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  QueryResultType,
  useBenchlingQuery,
  useSkippableBenchlingQuery,
  BenchlingQueryResult,
} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs, isInaccessible, getAccessibleOrInaccessibleId} from 'graphql-client/InaccessibleInfo';
import HelpButton from 'help/HelpButton';
import OOTBChartingCarousel from 'studies/typed/shared/OOTBChartingCarousel';
import {getProcedureRunUIStringForStudyType} from 'studies/TypedStudyUtils';
import {miniSpacingLeft} from 'styles/Utilities';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import {StudyInaccessibleContentBanner} from '../utils/ui/StudyQueryBanners';
import {
  ProcedureBasedStudyMultiRunAnalysisQuery,
  ProcedureBasedStudyMultiRunAnalysisQueryVariables,
} from './__generated/ProcedureBasedStudyMultiRunAnalysisQuery';
import {
  ProcedureRunsInStudiesMultiRunAnalysisQuery,
  ProcedureRunsInStudiesMultiRunAnalysisQueryVariables,
} from './__generated/ProcedureRunsInStudiesMultiRunAnalysisQuery';
import {INACCESSIBLE_STUDY_ERROR_TEXT, SELECTED_UNIT_OPERATIONS_LABEL} from './ProcedureRunAnalysisConstants';

export const INACCESSIBLE_RUNS_ERROR_TEXT =
  'Some runs are no longer accessible. Try refreshing the page if this is unexpected.';
export const OOTB_STUDY_DESCRIPTION = "Preview what we'll build to help you learn about your study.";

export function getInaccessibleRunsWarningText(studyType: StudyType): string {
  const runsString = getProcedureRunUIStringForStudyType(studyType, {plural: true});
  return `You do not have access to all ${runsString} in this study. This analysis will only include the ${runsString} that you have access to.`;
}

export type MultipleProcedureRunsSeedDataConfig = BaseSeedDataConfig<
  SeedDataConfigType.MULTIPLE_PROCEDURE_RUNS,
  {
    procedureRunIds: Array<string>;
  }
>;

export function validateMultiProcedureRunSeedDataConfigValid(
  config: MultipleProcedureRunsSeedDataConfig
): boolean {
  // None of the fields in MultipleProcedureRunsSeedDataConfig are editable, so the config is always valid.
  return true;
}

/**
 * Returns the study IDs for the queried procedure runs. Includes all study IDs (accessible and inaccessible).
 */
function getStudyIds(
  PMEIQueryResult: BenchlingQueryResult<ProcedureBasedStudyMultiRunAnalysisQuery>
): Array<string> {
  if (PMEIQueryResult.type !== QueryResultType.SUCCESS) {
    return [];
  }
  // We can't access the study field on an inaccessible procedure run, so we filter to accessible runs.
  const accessibleProcedureRuns = accessibleObjs(PMEIQueryResult.data.procedureRuns);
  return [...new Set(accessibleProcedureRuns.map((run) => getAccessibleOrInaccessibleId(run.study)))];
}

export function MultiProcedureRunSeedDataConfigEditor(props: {
  value: MultipleProcedureRunsSeedDataConfig;
  onChange: (value: MultipleProcedureRunsSeedDataConfig) => void;
}): JSX.Element {
  const PMEIQueryResult = useBenchlingQuery<
    ProcedureBasedStudyMultiRunAnalysisQuery,
    ProcedureBasedStudyMultiRunAnalysisQueryVariables
  >(PMEI_QUERY, {
    variables: {ids: props.value.data.procedureRunIds},
    renderCachedDataWhenRefetching: true,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const studyQueryResult = useSkippableBenchlingQuery<
    ProcedureRunsInStudiesMultiRunAnalysisQuery,
    ProcedureRunsInStudiesMultiRunAnalysisQueryVariables
  >(PROCEDURE_RUNS_IN_STUDIES_QUERY, {
    skip: PMEIQueryResult.type !== QueryResultType.SUCCESS,
    variables: {
      ids: getStudyIds(PMEIQueryResult),
    },
    // Note: future changes to this query's fetch policy should manually test the case where a study becomes
    // inaccessible after loading the overview and before attempting to generate an analysis. Some other fetch
    // policies have led to Apollo cache errors in this case before (Assertion Error: The Apollo cache
    // returned more than one result when querying fragments. Randomly returning first unordered result.)
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    renderCachedDataWhenRefetching: true,
  });
  return (
    <FormGroup>
      <FieldLabel label={SELECTED_UNIT_OPERATIONS_LABEL} />
      <HelpButton
        renderContent={() => (
          <p className={css(styles.helpToolTipText)}>
            Total instances is taken from the number of unit operations, conditions, and replicates.
          </p>
        )}
        iconClassName={css(styles.helpToolTip)}
        interaction={FloatingInteraction.HOVER}
      />
      {(() => {
        if (
          PMEIQueryResult.type === QueryResultType.PENDING ||
          PMEIQueryResult.type === QueryResultType.ERROR ||
          studyQueryResult.type !== QueryResultType.SUCCESS
        ) {
          return <CenteredHorizontalThrobber />;
        }

        // The user attempting to perform this action should have access to the requested runs. If they don't,
        // this may indicate that they lost access partway through the operation. This is an unexpected case,
        // but possible, so we show an error banner rather than asserting.
        if (
          accessibleObjs(PMEIQueryResult.data.procedureRuns).length !==
          PMEIQueryResult.data.procedureRuns.length
        ) {
          return <InlineBanner bannerType={InlineBannerType.ERROR} text={INACCESSIBLE_RUNS_ERROR_TEXT} />;
        }

        // Assumption: all runs to be analyzed are in the same study. We currently don't support cross-study
        // analysis, so we assert rather than showing an error banner - this should not be a possible state.
        assert(studyQueryResult.data.studies.length === 1, 'Expected all runs to be within the same study');
        const study = studyQueryResult.data.studies[0];

        // The user attempting to perform this action should have access to the relevant study. If they don't,
        // this may indicate that they lost access to the study partway through the operation. This is an
        // unexpected case, but possible, so we show an error banner rather than asserting.
        if (isInaccessible(study)) {
          return (
            <StudyInaccessibleContentBanner
              inaccessibleInfo={study}
              modelType={ModelTypeConstants.STUDY}
              displayErrorText={INACCESSIBLE_STUDY_ERROR_TEXT}
            />
          );
        }

        const inaccessibleRuns = study.procedureRuns.filter((run) => isInaccessible(run));

        const unitOperationCount = PMEIQueryResult.data.procedureRuns.reduce(
          (accumulator, run) =>
            !isInaccessible(run) ? accumulator + run.methodExecutionInstances.length : 0,
          0
        );

        return (
          <>
            <Row className={css(styles.instancesText)}>{unitOperationCount} instances</Row>
            {inaccessibleRuns.length > 0 && (
              <InlineBanner
                bannerType={InlineBannerType.WARNING}
                text={getInaccessibleRunsWarningText(study.studySchema.studyType)}
              />
            )}
            {/* TODO(BNCH-136861): Promote this check to the TypedStudyAdapter */}
            {study.studySchema.studyType === StudyType.PROCESS_DEVELOPMENT && (
              <div className={css(styles.ootbCarouselContainer)}>
                <OOTBChartingCarousel description={OOTB_STUDY_DESCRIPTION} />
              </div>
            )}
          </>
        );
      })()}
    </FormGroup>
  );
}

export const PMEI_QUERY: DocumentNode = gql`
  query ProcedureBasedStudyMultiRunAnalysisQuery($ids: [StrictString!]!)
  @skipAudit
  @engComponent(name: STUDIES) {
    procedureRuns(ids: $ids) {
      ... on ProcedureRunNode {
        id
        study {
          ... on StudyNode {
            id
          }
          ...InaccessibleInfoGraphQL
        }
        methodExecutionInstances {
          id
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const PROCEDURE_RUNS_IN_STUDIES_QUERY: DocumentNode = gql`
  query ProcedureRunsInStudiesMultiRunAnalysisQuery($ids: [StrictString!]!)
  @skipAudit
  @engComponent(name: STUDIES) {
    studies(ids: $ids) {
      ... on StudyNode {
        id
        procedureRuns {
          ... on ProcedureRunNode {
            id
          }
          ...InaccessibleInfoGraphQL
        }
        studySchema {
          id
          studyType
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const styles = StyleSheet.create({
  helpToolTip: miniSpacingLeft,
  helpToolTipText: {
    marginTop: '5px',
    marginLeft: '25px',
  },
  instancesText: {
    marginLeft: '3px',
  },
  ootbCarouselContainer: {
    // Stretch the carousel background to the edges of the modal border.
    // * 15px between unit-op count and the carousel. This matches the margin-bottom of FormGroup.
    // * Stretch flush against the sides of the modal body: -16px to offset the padding of the modal body.
    // * Stretch flush against the bottom of the modal body:
    //   (-15px to stretch outside the FormGroup in SeedDataRender)
    //   + (-16px to offset the padding of the modal body)
    margin: '15px -16px -31px -16px',
  },
});
